import { useEffect } from 'react';
import { Provider } from 'react-redux';
import { HistoryRouter as Router } from 'redux-first-history/rr6';
import { Navigate, Route, Routes } from 'react-router-dom';
import { persistStore } from 'redux-persist';
import { PersistGate } from 'redux-persist/integration/react';
import * as Sentry from '@sentry/browser';
import PropTypes from 'prop-types';

import ThemeProvider from 'Components/ThemeProvider';
import Version from 'Components/Version';
import RequireAuth from 'Components/RequireAuth';
import Variant from 'Components/Variant';
import Leaflet from 'Components/Leaflets';

import ScrollTopOnChange from 'Containers/ScrollTop';
import ErrorBoundary from 'Containers/ErrorBoundary';
import ProdItemLookUp from 'Containers/ProdItemLookUp';
import Helmet from 'Containers/Helmet';
import MagicAuth from 'Containers/MagicAuth';
import PageTracker from 'Containers/PageTracker';
import RedeemLanding from 'Containers/RedeemLanding';
import SigninContainer from 'Containers/SigninContainer';
import StylistLanding from 'Containers/StylistLanding';
import TriggerPageVisited from 'Containers/TriggerPageVisited';

import AccountApp from 'Apps/Account';
import CheckoutApp from 'Apps/Checkout';
import CheckoutCleaner from 'Apps/Checkout/Containers/CheckoutCleanerContainer';
import ConsultationApp from 'Apps/Consultation';
import FeedbackApp from 'Apps/Feedback';
import GiftApp from 'Apps/Gifting';
import InvalidReferralCode from 'Apps/Main/Scenes/InvalidReferralCode';
import MainApp from 'Apps/Main';
import MembershipApp from 'Apps/Membership';
import LandingPagesApp from 'Apps/LandingPages';
import ProductsApp from 'Apps/Products';
import SelfieApp from 'Apps/Selfie';
import PromotionTermsApp from 'Apps/PromotionTerms';

import { fetchAuthIfNeeded } from 'dux/auth/actions';
import { setUserCountry } from 'dux/user/actions';
import { stringToBool } from 'utils/envParsing';
import { pruneABTestLocalStorage } from 'abTesting/utils';
import getEnv from 'utils/getEnv';

/**
 * Import stripe as a side effect site wise to improve stripe's suspicious behavior detection capabalities
 * https://github.com/stripe/stripe-js#ensuring-stripejs-is-available-everywhere
 */
import '@stripe/stripe-js';

import 'what-input';
import store, { history } from './store';
import PrescriptionApp from './Apps/Prescription';

try {
  pruneABTestLocalStorage();
} catch (error) {
  Sentry.captureException(error);
}

const AppsRouter = ({ userGeolocationGuessedCountry }) => {
  useEffect(() => {
    // ensure to be fetching on the first state that gets rendered
    store.dispatch(fetchAuthIfNeeded()); // This will trigger feature flag fetch via middleware
    // Storing the user guessed country to make it accessible to all pages
    store.dispatch(setUserCountry(userGeolocationGuessedCountry));
  }, []);

  return (
    <Router history={history}>
      <ThemeProvider>
        <ErrorBoundary>
          <Version
            initiallyVisible={stringToBool(getEnv('REACT_APP_SHOW_VERSION'))}
            versionAlgo={getEnv('REACT_APP_VERSION_ALGO')}
            versionApi={getEnv('REACT_APP_VERSION_API')}
            versionSite={getEnv('REACT_APP_VERSION_WWW')}
          />
          <Routes>
            <Route
              element={
                <>
                  <Helmet />
                  <PageTracker />
                  <RequireAuth shouldRedirect={false}>
                    <TriggerPageVisited />
                  </RequireAuth>
                </>
              }
              path="*"
            />
          </Routes>
          <Routes>
            {/*
          ScrollTop - uses react router to define when a scroll top should occour
          Normal behaviour is to scroll top after pathname changes
        */}
            <Route element={null} path="/checkout/contact-address" />
            <Route element={null} path="/checkout/payment" />
            <Route element={<ScrollTopOnChange />} path="*" />
          </Routes>
          <Routes>
            {/* Hacky component to clear redux store after Checkout + Selfie */}
            <Route element={<CheckoutCleaner />} path="/checkout/*" />
            <Route element={<CheckoutCleaner />} path="/selfie/*" />
          </Routes>
          <Routes>
            <Route element={<SigninContainer />} path="/signin" />
            <Route
              element={
                <RequireAuth>
                  <AccountApp />
                </RequireAuth>
              }
              path="/account/*"
            />
            <Route element={<MagicAuth />} path="/auth/signin" />
            <Route element={<SelfieApp />} path="/selfie/*" />
            <Route
              element={
                <RequireAuth>
                  <CheckoutApp />
                </RequireAuth>
              }
              path="/checkout/*"
            />
            <Route element={<ConsultationApp />} path="/consultation/*" />
            <Route element={<ConsultationApp />} path="/supplements-consultation/*" />
            <Route element={<PrescriptionApp />} path="/results/*" />
            <Route
              element={
                <RequireAuth>
                  <FeedbackApp />
                </RequireAuth>
              }
              path="/feedback/*"
            />
            <Route
              element={
                <RequireAuth>
                  <FeedbackApp />
                </RequireAuth>
              }
              path="/supplements-feedback/*"
            />
            <Route element={<GiftApp />} path="/gift/*" />
            <Route element={<LandingPagesApp />} path="/pages/*" />
            <Route element={<Navigate replace to="/pages/oil" />} path="/oil" />
            <Route path="/v">
              <Route element={<Variant />} path=":variantPubKey/*" />
              <Route element={<Navigate replace to="/products" />} index />
            </Route>
            <Route path="/l">
              <Route element={<Leaflet />} path=":orderBoxPubKey/*" />
              <Route element={<Navigate replace to="/" />} index />
            </Route>
            <Route element={<Navigate replace to="/products" />} path="/f/*" />
            <Route element={<MembershipApp />} path="/the-salon/*" />
            <Route element={<ProductsApp />} path="/products/*" />
            <Route element={<StylistLanding />} path="/stylist/:slug" />
            <Route path="/redeem">
              <Route element={<InvalidReferralCode />} path="invalid-code" />
              <Route element={<RedeemLanding />} index />
            </Route>
            <Route
              element={
                <RequireAuth>
                  <ProdItemLookUp />
                </RequireAuth>
              }
              path="/p/:productionItemPubkey"
            />
            <Route element={<PromotionTermsApp />} path="/promotion-terms/*" />
            <Route element={<MainApp />} path="/*" />
          </Routes>
        </ErrorBoundary>
      </ThemeProvider>
    </Router>
  );
};

AppsRouter.propTypes = {
  userGeolocationGuessedCountry: PropTypes.string.isRequired,
};

const persistor = persistStore(store);

const Root = ({ userGeolocationGuessedCountry }) => (
  <Provider store={store}>
    <PersistGate loading={null} persistor={persistor}>
      <AppsRouter userGeolocationGuessedCountry={userGeolocationGuessedCountry} />
    </PersistGate>
  </Provider>
);

Root.propTypes = {
  userGeolocationGuessedCountry: PropTypes.string.isRequired,
};

export default Root;
